// @ts-strict-ignore
import {inject, Injectable, Injector, Signal, signal} from "@angular/core";
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {Assessment, AssessmentItem, AssessmentState, ChecklistItemType, CompetenceCategory, UserRecordingAssessment} from "@models";
import {GeneratedTypePredicates, SimAssessmentService} from "@services";
import {PointInputValidator} from "./point-input.validator";
import {debounceTime, distinctUntilChanged, filter, map, startWith, switchMap} from "rxjs/operators";
import {combineLatest, defer, merge, Observable, of, OperatorFunction, tap} from "rxjs";
import {merge as lodashMerge} from "lodash-es";
import {isAssessableGroupActive} from "@common/components/assessment/utils";
import {toLazySignal} from "ngxtension/to-lazy-signal";
import {toObservable} from "@angular/core/rxjs-interop";

export abstract class AssessableAnswerService {
	public abstract isGroupActivated(groupId: string): boolean;
	public abstract answeredItemsMap: Signal<Map<string, Map<string, boolean>>>;
}

type AssessmentAnswerForm = FormGroup<{
	assessableGroups: FormArray<
		FormGroup<{
			// required for tracking the group
			id: FormControl<string>;
			items: FormArray<ItemFormGroup>;
		}>
	>;
	ungroupedItems: FormArray<ItemFormGroup>;
	note: FormControl<string>;
}>;

type ItemFormGroup = FormGroup<{
	// required for tracking the item
	id: FormControl<string>;
	answer: FormControl<any>;
	freeTextScore: FormControl<number>;
}>;

function formControlSavingPipeline<T, V, U>(
	observable: Observable<T>,
	transformations: OperatorFunction<T, V>,
	savingCallback: (value: V) => Promise<U>,
) {
	return observable.pipe(
		transformations,
		switchMap((value) => of(savingCallback(value))),
	);
}

@Injectable()
export class AssessmentAnswerService extends AssessableAnswerService {
	// Injections
	readonly #fb = inject(FormBuilder);
	readonly #assessmentService = inject(SimAssessmentService);
	readonly #injector = inject(Injector);

	readonly #isInitialized = signal(false);
	readonly #answeredItemsMap = signal(new Map<string, Map<string, boolean>>());
	readonly #answerObservables: Array<ItemFormGroup["valueChanges"]> = [];

	// Initialized in the initialize method
	#assessment!: Signal<Assessment | UserRecordingAssessment>;
	public form!: AssessmentAnswerForm;

	public isInitialized = this.#isInitialized.asReadonly();
	public answeredItemsMap = this.#answeredItemsMap.asReadonly();
	public cachedCompetencyCategories = toLazySignal<CompetenceCategory[]>(
		defer(() =>
			toObservable(this.#assessment, {injector: this.#injector}).pipe(
				map((v) => (GeneratedTypePredicates.isUserRecordingAssessment(v) ? v.assignedCompetenceCategories : undefined)),
			),
		),
	);

	public $savingPipeline = combineLatest([
		defer(() => this.form.valueChanges.pipe(startWith(this.form.value))).pipe(tap((value) => this.fillAnsweredItemsMap(value))),
		formControlSavingPipeline(
			defer(() => merge(...this.#answerObservables)),
			(observable) => observable.pipe(filter((_) => this.canBeSaved(this.#assessment()))),
			// remove the Partial<T> here, we know that the value is complete
			(value) => this.saveAnswer(value as ItemFormGroup["value"]),
		),
		formControlSavingPipeline(
			defer(() => this.form.controls.note.valueChanges),
			(observable) =>
				observable.pipe(
					filter((_) => this.canBeSaved(this.#assessment())),
					debounceTime(500),
					distinctUntilChanged(),
				),
			(value) => this.saveNote(value),
		),
	]);

	public $assessable: Observable<Assessment> = combineLatest([
		defer(() => toObservable(this.#assessment, {injector: this.#injector})),
		defer(() => this.form.valueChanges.pipe(startWith(null))),
	]).pipe(
		// do not use the form value from the combineLatest, as it might not be in sync with the form
		map(([assessment]) => this.getModelWithFormState(assessment, this.form.value)),
	);

	public initialize(assessmentSignal: Signal<Assessment | UserRecordingAssessment>) {
		if (this.#isInitialized()) {
			return;
		}
		this.#assessment = assessmentSignal;
		const assessment = this.#assessment();

		const createItemControlAndSubscribe = (item: AssessmentItem) => {
			const itemControl = this.createItemControl(item);
			this.#answerObservables.push(itemControl.valueChanges);
			return itemControl;
		};

		this.form = this.#fb.group({
			note: this.#fb.control(GeneratedTypePredicates.isUserRecordingAssessment(assessment) ? assessment.note : ""),
			assessableGroups: this.#fb.array(
				assessment.assessableGroups.map((group) =>
					this.#fb.group({
						id: group.groupId,
						items: this.#fb.array(group.items.map(createItemControlAndSubscribe)),
					}),
				),
			),
			ungroupedItems: this.#fb.array(assessment.ungroupedItems.map(createItemControlAndSubscribe)),
		});

		this.#isInitialized.set(true);
	}

	public findItemControl(assessmentItemId: string) {
		const groupedItemControls = this.form.controls.assessableGroups.controls.flatMap((g) => g.controls.items.controls);
		const ungroupedItemControls = this.form.controls.ungroupedItems.controls;
		const itemControls = [...groupedItemControls, ...ungroupedItemControls];
		return itemControls.find((c) => c.value.id === assessmentItemId);
	}

	public isGroupActivated(groupId: string): boolean {
		const assessment = this.getModelWithFormState(this.#assessment(), this.form.value);
		return isAssessableGroupActive(groupId, assessment);
	}

	private canBeSaved(assessment: Assessment) {
		return GeneratedTypePredicates.isUserRecordingAssessment(assessment) && assessment.state === AssessmentState.InProgress;
	}

	private createItemControl(item: AssessmentItem) {
		return this.#fb.group({
			id: this.#fb.control(item.assessableItemId),
			answer: this.#fb.control(item.answer),
			freeTextScore: this.#fb.control(
				{
					value: item.freeTextScore,
					disabled: item.type !== ChecklistItemType.FreeAnswerOption,
				},
				[PointInputValidator, Validators.min(0), Validators.max(item.freeTextMaxScore)],
			),
		});
	}

	/**
	 * Gets the model overwritten by the form state.
	 * Assumes that the every item in the form is also present in the model.
	 * Does not itself update the form state or the model.
	 */
	private getModelWithFormState(assessment: Assessment, formValue: AssessmentAnswerForm["value"]) {
		const mergedAssessment = lodashMerge({}, assessment, formValue);
		mergedAssessment.assessableGroups = formValue.assessableGroups.map(mapGroup);
		mergedAssessment.ungroupedItems = formValue.ungroupedItems.map(mapItem);
		return mergedAssessment;

		// local functions
		function mapItem(formItem: (typeof formValue.ungroupedItems)[0]) {
			const items = assessment.assessableGroups.flatMap((g) => g.items).concat(assessment.ungroupedItems);
			const modelItem = items.find((i) => i.assessableItemId === formItem.id);
			if (!modelItem) {
				throw new Error("Item not found in model");
			}

			return lodashMerge({}, modelItem, formItem);
		}

		function mapGroup(formGroup: (typeof formValue.assessableGroups)[0]) {
			const modelGroup = assessment.assessableGroups.find((g) => g.groupId === formGroup.id);
			if (!modelGroup) {
				throw new Error("Group not found in model");
			}

			const mergedGroup = lodashMerge({}, modelGroup, formGroup);
			mergedGroup.items = formGroup.items.map(mapItem);
			return mergedGroup;
		}
	}

	private async saveNote(noteValue: string) {
		const assessment = this.#assessment();
		return await this.#assessmentService.updateAssessmentNote({
			note: noteValue,
			userRecordingAssessmentId: assessment.id,
		});
	}

	private async saveAnswer(answerValue: ItemFormGroup["value"]) {
		const assessment = this.#assessment();
		return await this.#assessmentService.updateAnswer({
			answer: answerValue.answer,
			assessmentItemId: answerValue.id,
			userRecordingAssessmentId: assessment.id,
			freeTextScore: answerValue.freeTextScore,
		});
	}

	private fillAnsweredItemsMap(value: typeof this.form.value) {
		const answeredItemsMap = new Map<string, Map<string, boolean>>();
		for (const group of value.assessableGroups) {
			answeredItemsMap.set(group.id, new Map<string, boolean>());
			for (const {id, answer} of group.items) {
				const isAnswered = (Array.isArray(answer) && answer.length > 0) || (answer !== undefined && answer !== null);
				answeredItemsMap.get(group.id).set(id, isAnswered);
			}
		}
		this.#answeredItemsMap.set(answeredItemsMap);
	}
}
